/* Permission codes.
 *
 * These mirror the `code` values in the server's catalogue
 * (GET /api/admin/role-management/permissions, declared in
 * node-3c-ecommerce/src/utils/permissions.js). They are used ONLY to tag nav
 * items and routes with the permission needed to open them.
 *
 * WHICH ROLE HOLDS WHICH CODE is never stored here -- it is a database table the
 * client edits at runtime from Roles & Permissions, so any copy kept in the
 * portal would be wrong the moment they press Save. The portal reads the flat
 * `permissions` array that /admin/auth/me returns and nothing else.
 */
export const PERMISSION = {
    // Staff & access
    USERS_VIEW: "users.view",
    USERS_MANAGE: "users.manage",
    ROLES_VIEW: "roles.view",
    ROLES_MANAGE: "roles.manage",
    // Customers & retailers
    CUSTOMERS_VIEW: "customers.view",
    CUSTOMERS_MANAGE: "customers.manage",
    RETAILERS_VIEW: "retailers.view",
    RETAILERS_MANAGE: "retailers.manage",
    // Product catalogue
    PRODUCTS_VIEW: "products.view",
    PRODUCTS_MANAGE: "products.manage",
    PRODUCTS_IMPORT: "products.import",
    REDEMPTION_PRODUCTS_VIEW: "redemption_products.view",
    REDEMPTION_PRODUCTS_MANAGE: "redemption_products.manage",
    STOCK_SETTINGS_MANAGE: "stock_settings.manage",
    // Cards & coin packages
    CARDS_VIEW: "cards.view",
    CARDS_MANAGE: "cards.manage",
    CARDS_GENERATE: "cards.generate",
    CARDS_ASSIGN: "cards.assign",
    CARDS_EXPORT: "cards.export",
    // Orders
    ORDERS_VIEW: "orders.view",
    ORDERS_FULFIL: "orders.fulfil",
    ORDERS_CANCEL: "orders.cancel",
    ORDERS_EXPORT: "orders.export",
    REDEMPTION_ORDERS_VIEW: "redemption_orders.view",
    REDEMPTION_ORDERS_CANCEL: "redemption_orders.cancel",
    PRIZE_ORDERS_FULFIL: "prize_orders.fulfil",
    // Money
    TRANSACTIONS_VIEW: "transactions.view",
    BANK_TRANSFERS_APPROVE: "bank_transfers.approve",
    REFERRALS_VIEW: "referrals.view",
    REFERRAL_SETTINGS_MANAGE: "referral_settings.manage",
    // Customer Service. `.view` opens the inbox and the thread; `.reply` is
    // separate on purpose, because being allowed to end a ticket should not imply
    // being allowed to speak to a customer in the company's voice.
    SUPPORT_TICKETS_VIEW: "support_tickets.view",
    SUPPORT_TICKETS_REPLY: "support_tickets.reply",
    SUPPORT_TICKETS_MANAGE: "support_tickets.manage",
    // Campaigns & Mystery Box
    CAMPAIGNS_VIEW: "campaigns.view",
    CAMPAIGN_PRIZES_MANAGE: "campaign_prizes.manage",
    MYSTERY_BOX_VIEW: "mystery_box.view",
    MYSTERY_BOX_MANAGE: "mystery_box.manage",
    MYSTERY_BOX_SETTINGS: "mystery_box.settings",
    BIG_PRIZES_MANAGE: "big_prizes.manage",
};

/* `can(code)` for a given permission array. A missing code on an item means
   "everyone signed in", which is why an absent `perm` returns true. */
export const makeCan = (permissions) => {
    const set = new Set(permissions || []);
    return (perm) => !perm || set.has(perm);
};

export default PERMISSION;
